import * as SentrySDK from '@sentry/browser';
import { ThunkRejectedError } from '../store/ThunkRejectedError';

function isNoise(exception: unknown) {
  if (typeof exception === 'object' && exception !== null && 'name' in exception && exception.name === 'AbortError') {
    return true;
  }
  // An API error body carries no message once serialized; alerting() already showed it, HttpClient reports 5xx
  return exception instanceof ThunkRejectedError && !exception.serialized.message;
}

// Remote Config falls back to RemoteConfig.CONFIG_DEFAULTS when Firebase is down
function isRemoteConfigOutage(event: SentrySDK.ErrorEvent) {
  return !!event.request?.url?.startsWith('https://firebaseremoteconfig.googleapis.com/');
}

// Google Maps posting its own telemetry: a failed log upload leaves the map working
function isGoogleMapsTelemetry(event: SentrySDK.ErrorEvent) {
  return !!event.request?.url?.startsWith('https://maps.googleapis.com/maps_api_js_slo/log');
}

export default class Sentry {
  static isCollectingData = import.meta.env.PROD;

  private static instance: Sentry | null = null;

  static CATEGORIES = {
    BUTTON_CLICK: 'BUTTON_CLICK',
    LINK_CLICK: 'LINK_CLICK',
    TRANSLATION: 'TRANSLATION',
    TOOLTIP_CLICK: 'TOOLTIP_CLICK',
    EVENT: 'EVENT',
  } as const;

  static EVENT_LEVELS = {
    Fatal: 'fatal',
    Error: 'error',
    Warning: 'warning',
    Log: 'log',
    Info: 'info',
    Debug: 'debug',
    Critical: 'critical',
  } as const;

  init() {
    if (Sentry.isCollectingData) {
      SentrySDK.init({
        dsn: 'https://0447629153494b98b1c08fbff2714308@o404951.ingest.sentry.io/5269789',
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'Load failed', // Thrown by Apple devices and concerns tracking api
          'Object captured as promise rejection with keys: errors', // Ghost error filling the quota, but never seen anywhere other than Sentry
          'Failed to fetch',
          'NetworkError when attempting to fetch resource',
          // Google Maps scripts failing to download, blocked or offline: the key and quota errors read differently
          /^Could not load "\w+"\.$/,
          'window.google not found after 10 seconds',
          // lottie-web finishing a request for an animation the Loader already destroyed
          /'__complete'|"__complete"/,
          /evaluating '\w+\.onComplete'|reading 'onComplete'|property "onComplete"/,
        ],
        environment: import.meta.env['VITE_SENTRY_ENVIRONMENT'],
        beforeSend: (event, hint) =>
          isNoise(hint.originalException) || isRemoteConfigOutage(event) || isGoogleMapsTelemetry(event) ? null : event,
        integrations: [SentrySDK.httpClientIntegration()],
        // This option is required for capturing headers and cookies.
        sendDefaultPii: true,
      });
      SentrySDK.getCurrentScope().setTag('version', import.meta.env['VITE_VERSION']);
    }
  }

  reportError(error: unknown) {
    if (error && Sentry.isCollectingData) {
      SentrySDK.captureException(error);
    }
  }

  logEvent(event: SentrySDK.Breadcrumb) {
    if (event && Sentry.isCollectingData) {
      SentrySDK.addBreadcrumb(event);
    }
  }

  setUser(user: SentrySDK.User) {
    if (user && Sentry.isCollectingData) {
      SentrySDK.setUser(user);
    }
  }

  captureMessage(message: string) {
    if (message && Sentry.isCollectingData) {
      SentrySDK.captureMessage(message, 'error');
    }
  }

  static getInstance() {
    if (!Sentry.instance) {
      Sentry.instance = new Sentry();
    }
    return Sentry.instance;
  }
}
